import * as amplitude from '@amplitude/analytics-browser';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { useMediaCenterPathContext } from 'hb-react/frontoffice/apps/MediaCenter/contexts/MediaCenterPathContext';
import {
  MEDIA_CENTER_RAILS_TYPE,
  MEDIA_CENTER_QUERY_KEYS,
} from 'hb-react/frontoffice/apps/MediaCenter/utils/queryUtils';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import toggleLike from 'hb-react/shared/components/DesignSystem/Reactions/utils/toggleLike';
import FileThumbnail from 'hb-react/shared/components/Thumbnails/FileThumbnail';
import { formatFileExtension } from 'hb-react/shared/components/Thumbnails/FileThumbnail/utils/fileUtils';
import useNavigatorShare from 'hb-react/shared/hooks/useNavigatorShare';
import { isCommentsModuleAvailable } from 'hb-react/shared/utils/features/comments';

import { getActionItems } from './utils/fileThumbnailUtils';
import { actionItemsIcons } from '../actionItemsIcons';

const FilesRenderer = ({ item, blockId }) => {
  const { mediaCenterFilePath } = useMediaCenterPathContext();

  return (
    <MediaCenterFileThumbnail
      blockId={blockId}
      file={item}
      mediaCenterFilePath={mediaCenterFilePath}
      invalidateQuery={null}
    />
  );
};

export const MediaCenterFileThumbnail = ({
  file,
  mediaCenterFilePath,
  blockId,
}) => {
  const { currentNetwork, currentUser } = useDefaultAppContexts();
  const queryClient = useQueryClient();

  const {
    updated_at: updatedAt,
    total_likes: totalLikes,
    total_comments: totalComments,
    current_user_like_id: currentUserLikeId,
    file_type: fileType,
    name,
    url,
    uuid,
    id,
  } = file;
  const navigate = useNavigate();
  const { search } = window.location;

  const fileUrl = mediaCenterFilePath(uuid);

  const openPlayVideo = () => {
    navigate(fileUrl + search, { state: { play: true } });
  };

  const sharedResourceUrl = `${window.location.origin}${fileUrl}`;

  const { handleShare, shareUrl } = useNavigatorShare({
    data: {
      text: name,
      url: sharedResourceUrl,
    },
    onShare: () => {
      amplitude.track('MediaCenter|FileShared');
    },
  });

  const onLikeCallback = () => {
    queryClient.removeQueries({
      queryKey: [MEDIA_CENTER_QUERY_KEYS.likes, id],
    });
    if (blockId) {
      queryClient.invalidateQueries({
        queryKey: [blockId],
      });
    }
  };

  return (
    <FileThumbnail
      bottom={{
        text: name,
      }}
      header={{
        actions: getActionItems(
          file,
          `${window.location.origin}${fileUrl}`,
          actionItemsIcons,
        ),
        withUserInformations: false,
        date: updatedAt,
      }}
      tag={formatFileExtension(file)}
      reactions={{
        comments: {
          onClick: () => {
            navigate(fileUrl + search);
          },
          totalComments: totalComments || 0,
          dataTestId: 'media-center-link-comment',
        },
        likes: {
          initialLikes: {
            likeable_id: id,
            count: totalLikes || 0,
            id: currentUserLikeId || null,
            users: [],
          },
          onLikeCallback,
          onTogglePostLike: toggleLike,
          type: MEDIA_CENTER_RAILS_TYPE,
        },
        shares: {
          onClick: handleShare,
          shareUrl,
        },
        showFullText: false,
      }}
      showReactions={isCommentsModuleAvailable(
        currentUser,
        !currentNetwork?.network_configuration?.media_center?.comments_enabled,
      )}
      content={{
        openPlayVideo,
        file,
        fileUrl,
        to: {
          search,
        },
      }}
      link={{
        fileType,
        to: {
          pathname: fileUrl,
          search,
        },
        url,
      }}
    />
  );
};

export default FilesRenderer;
